// src/components/settings/SettingsNav.tsx — навигация настроек: группы, поиск, индикаторы интеграций
import React, { useMemo, useState } from "react";

const ADMIN_ONLY = ["ADMIN", "OFFICE_LEAD"];
const ALL = ["ADMIN", "OFFICE_LEAD", "SENIOR_OFFICE", "MANAGER", "ACCOUNTANT"];

export interface NavItem {
  id: string;
  label: string;
  icon: string;
  desc: string;
  roles: string[];
  /** имя интеграции в базе: по нему показывается индикатор «подключено» */
  integration?: string;
  /** дополнительные слова для поиска */
  keywords?: string;
}
export interface NavGroup {
  title: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    title: "Компания",
    items: [
      { id: "organization", label: "Моя организация", icon: "🏢", roles: ADMIN_ONLY, desc: "Реквизиты, банк, нумерация документов, логотип, печать и подпись", keywords: "бин иин адрес телефон банк счёт реквизиты логотип печать подпись" },
      { id: "users", label: "Пользователи", icon: "👥", roles: ADMIN_ONLY, desc: "Сотрудники, роли и доступ (в том числе роль «Кассир»)", keywords: "сотрудники роли кассир доступ пароль" },
      { id: "contracts", label: "Договоры", icon: "📄", roles: ALL, desc: "Шаблоны договоров и документов", keywords: "шаблоны договор акт счёт" },
    ],
  },
  {
    title: "Продажи и клиенты",
    items: [
      { id: "pipeline", label: "Воронки", icon: "🔀", roles: ADMIN_ONLY, desc: "Воронки сделок и их стадии", keywords: "стадии сделки канбан" },
      { id: "paymentTypes", label: "Типы оплаты", icon: "💳", roles: ADMIN_ONLY, desc: "Способы оплаты, доступные в сделках", keywords: "наличные карта kaspi перевод" },
      { id: "notifications", label: "Уведомления WA", icon: "💬", roles: ADMIN_ONLY, integration: "whatsapp", desc: "Шаблоны WhatsApp-сообщений клиентам и менеджерам по этапам сделки", keywords: "whatsapp ватсап шаблоны сообщения kazinfoteh" },
    ],
  },
  {
    title: "Каталог",
    items: [
      { id: "productCategories", label: "Категории товаров", icon: "🗂", roles: ALL, desc: "Папки и категории каталога, названия на русском и казахском", keywords: "папки каталог казахский" },
      { id: "products", label: "Товары", icon: "📦", roles: ALL, desc: "Товары, услуги и ПО: цены, штрихкоды, остатки, ключи", keywords: "цены штрихкод sku артикул" },
      { id: "alStyle", label: "Al-Style", icon: "🛒", roles: ADMIN_ONLY, integration: "al-style-settings", desc: "Подключение каталога дистрибьютора Al-Style", keywords: "alstyle дистрибьютор поставщик api ключ" },
    ],
  },
  {
    title: "Интеграции",
    items: [
      { id: "ukassa", label: "uKassa", icon: "🧾", roles: ADMIN_ONLY, integration: "ukassa", desc: "Онлайн-касса: фискальные чеки, кассы и секции по воронкам", keywords: "ukassa чек касса фискальный ккм" },
      { id: "kaspi", label: "Kaspi Магазин", icon: "🔴", roles: ADMIN_ONLY, integration: "kaspi", desc: "Kaspi: заказы по статусам, остатки и цены XML-прайс-листом", keywords: "kaspi каспи маркетплейс заказы остатки прайс" },
      { id: "halykMarket", label: "HalykMarket", icon: "🏬", roles: ADMIN_ONLY, integration: "halyk_market", desc: "Маркетплейс Halyk: заказы, наличие, журнал обмена", keywords: "halyk маркетплейс заказы" },
      { id: "monttech", label: "MONT", icon: "🔑", roles: ADMIN_ONLY, integration: "monttech", desc: "Получение лицензионных ключей у поставщика MONT", keywords: "mont монт ключи лицензии" },
      { id: "siteSettings", label: "Настройки сайта", icon: "🌐", roles: ADMIN_ONLY, integration: "site_settings", desc: "Сайт-магазин: стадии заказов, оплата, платёжная система", keywords: "сайт магазин robokassa оплата" },
      { id: "mail", label: "Почта", icon: "✉️", roles: ADMIN_ONLY, integration: "mail", desc: "Отправка писем: сервер, логин, тестовое письмо", keywords: "smtp email письма" },
    ],
  },
];

export const allNavItems = (role: string): NavItem[] =>
  NAV_GROUPS.flatMap((g) => g.items).filter((i) => i.roles.includes(role));

interface Props {
  role: string;
  active: string;
  onChange: (id: string) => void;
  /** имя интеграции -> включена ли */
  status: Record<string, boolean>;
}

/** Боковая навигация (десктоп) и выпадающий список (телефон) */
export default function SettingsNav({ role, active, onChange, status }: Props) {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();

  const groups = useMemo(
    () =>
      NAV_GROUPS.map((g) => ({
        title: g.title,
        items: g.items.filter(
          (i) =>
            i.roles.includes(role) &&
            (!term || `${i.label} ${i.desc} ${i.keywords ?? ""} ${g.title}`.toLowerCase().includes(term)),
        ),
      })).filter((g) => g.items.length),
    [role, term],
  );

  const dot = (i: NavItem) => {
    if (!i.integration) return null;
    const on = status[i.integration];
    return (
      <span
        className={`st2-dot ${on ? "on" : "off"}`}
        title={on ? "Подключено" : "Не настроено или выключено"}
        aria-label={on ? "Подключено" : "Не настроено"}
      />
    );
  };

  return (
    <>
      {/* Телефон: компактный выбор раздела */}
      <div className="st2-mobile">
        <select className="st2-select" value={active} onChange={(e) => onChange(e.target.value)}>
          {NAV_GROUPS.map((g) => {
            const items = g.items.filter((i) => i.roles.includes(role));
            return items.length ? (
              <optgroup key={g.title} label={g.title}>
                {items.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.icon} {i.label}
                  </option>
                ))}
              </optgroup>
            ) : null;
          })}
        </select>
      </div>

      {/* Десктоп: боковое меню */}
      <nav className="st2-nav" aria-label="Разделы настроек">
        <div className="st2-search">
          <input
            className="st2-search-input"
            placeholder="Найти настройку…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && groups[0]?.items[0]) {
                onChange(groups[0].items[0].id);
                setQ("");
              }
              if (e.key === "Escape") setQ("");
            }}
          />
        </div>
        {groups.length === 0 && <div className="st2-none">Ничего не найдено</div>}
        {groups.map((g) => (
          <div key={g.title} className="st2-group">
            <div className="st2-group-title">{g.title}</div>
            {g.items.map((i) => (
              <button
                key={i.id}
                type="button"
                className={`st2-item ${active === i.id ? "active" : ""}`}
                onClick={() => onChange(i.id)}
                title={i.desc}
              >
                <span className="st2-ico">{i.icon}</span>
                <span className="st2-label">{i.label}</span>
                {dot(i)}
              </button>
            ))}
          </div>
        ))}
      </nav>
    </>
  );
}
